import { z } from "zod";
import { LocaleCodeSchema } from "./locale.schemas.js";
import type {
  JsonObjectType,
  JsonValueType,
} from "../types/json-value.types.js";
import type { TranslationKey } from "../types/translation-key.types.js";

/**
 * LocalizedTextSchema — Zod schema for multilingual text content.
 *
 * Validates:
 * - lang: must be a valid LocaleCode
 * - content: the text in the primary language
 * - translations: optional record of LocaleCode → string
 */
export const LocalizedTextSchema = z.object({
  lang: LocaleCodeSchema,
  content: z.string(),
  translations: z.record(z.string(), z.string()).optional(),
});

export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

const templateParamsSchema: z.ZodType<
  Record<string, JsonValueType>,
  JsonObjectType
> = z.record(z.string(), z.json());

export const LocalizedTextTemplateSchema = z.object({
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TranslationKey is a federated conditional type that can't be expressed as a Zod schema directly
  key: z.string() as z.ZodType<TranslationKey, string>,
  params: templateParamsSchema.optional(),
  locale: LocaleCodeSchema,
});

export type LocalizedTextTemplate = z.infer<typeof LocalizedTextTemplateSchema>;

export type LocalizedTextOrTemplate = LocalizedText | LocalizedTextTemplate;
